import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLang } from '../contexts/LangContext';
import { useAuth } from '../contexts/AuthContext';
import { accountsApi, CreateAccountData, PermissionGroupOption, Operator } from '../api/accountsApi';
import { buildingsApi, Building } from '../api/buildingsApi';
import { getApiErrorMessage } from '../utils/apiErrors';

export const AddAccount: React.FC = () => {
  const { t } = useLang();
  const { isSuperAdmin } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [loadingData, setLoadingData] = useState(true);
  const [permissionGroups, setPermissionGroups] = useState<PermissionGroupOption[]>([]);
  const [operators, setOperators] = useState<Operator[]>([]);
  const [buildings, setBuildings] = useState<Building[]>([]);
  const [applyAllBuildings, setApplyAllBuildings] = useState(false);
  const [formData, setFormData] = useState<CreateAccountData>({
    name: '',
    email: '',
    phoneNumber: '',
    username: '',
    password: '',
    role: 'STAFF',
    isActive: true,
    permissionGroupId: '',
    operatorId: '',
  });

  useEffect(() => {
    loadInitialData();
  }, []);

  useEffect(() => {
    if (formData.role !== 'STAFF') {
      return;
    }

    if (isSuperAdmin && !formData.operatorId) {
      setPermissionGroups([]);
      setFormData((prev) => ({ ...prev, permissionGroupId: '' }));
      return;
    }

    loadPermissionGroups('STAFF', isSuperAdmin ? formData.operatorId : undefined);
  }, [formData.role, formData.operatorId, isSuperAdmin]);

  const loadInitialData = async () => {
    try {
      setLoadingData(true);
      const [operatorsData, buildingsData] = await Promise.all([
        isSuperAdmin ? accountsApi.getOperators() : Promise.resolve([]),
        buildingsApi.getBuildings(),
      ]);
      setOperators(operatorsData);
      setBuildings(buildingsData);
    } catch (error) {
      console.error('Error loading data:', error);
      alert(t('errorLoadingData'));
    } finally {
      setLoadingData(false);
    }
  };

  const loadPermissionGroups = async (role: string, operatorId?: string) => {
    try {
      // Load permission groups for SUPERADMIN and STAFF roles
      if (role === 'SUPERADMIN' || role === 'STAFF') {
        const groupsData = await accountsApi.getPermissionGroups(role, operatorId);
        setPermissionGroups(groupsData);
      } else {
        // Clear permission groups for other roles
        setPermissionGroups([]);
      }
    } catch (error) {
      console.error('Error loading permission groups:', error);
      setPermissionGroups([]);
    }
  };

  const handleInputChange = (field: keyof CreateAccountData, value: string | boolean) => {
    setFormData((prev: CreateAccountData) => {
      const newData = { ...prev, [field]: value };
      
      // Clear permission group and operator when role changes
      if (field === 'role') {
        newData.permissionGroupId = '';
        newData.operatorId = '';
        
        // Load permission groups for the new role
        if (typeof value === 'string' && value !== 'STAFF') {
          loadPermissionGroups(value);
        }
      }

      if (field === 'operatorId') {
        newData.permissionGroupId = '';
      }
      
      return newData;
    });
  };

  const handleBuildingIdsChange = (ids: string[]) => {
    setFormData((prev: CreateAccountData) => ({
      ...prev,
      buildingIds: ids,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validation
    if (!formData.name.trim()) {
      alert(t('pleaseEnterFullName'));
      return;
    }
    if (!formData.email.trim()) {
      alert(t('pleaseEnterEmail'));
      return;
    }
    if (!formData.username.trim()) {
      alert(t('pleaseEnterUsername'));
      return;
    }
    if (!formData.password.trim()) {
      alert(t('pleaseEnterPassword'));
      return;
    }
    if (!formData.role) {
      alert(t('pleaseSelectRole'));
      return;
    }

    // Role-specific validation
    if (formData.role === 'STAFF') {
      if (!formData.permissionGroupId) {
        alert(t('pleaseSelectPermissionGroup'));
        return;
      }
      if (isSuperAdmin && !formData.operatorId) {
        alert(t('pleaseSelectOperator'));
        return;
      }
    }

    try {
      setLoading(true);
      const submitData = { ...formData };
      // For STAFF role, transform building selection per UI
      if (formData.role === 'STAFF') {
        const selected = (formData.buildingIds as string[] | undefined) || [];
        submitData.buildingIds = applyAllBuildings ? buildings.map(b => b.id) : selected;
      } else {
        delete submitData.buildingIds;
      }
      
      // Clean up data based on role
      if (formData.role === 'OPERATOR' || formData.role === 'RESIDENT') {
        delete submitData.permissionGroupId;
        delete submitData.operatorId;
      } else if (formData.role === 'SUPERADMIN') {
        // For SUPERADMIN, don't send operatorId (should be null)
        delete submitData.operatorId;
        if (!formData.permissionGroupId) {
          delete submitData.permissionGroupId;
        }
      } else if (formData.role === 'STAFF' && !isSuperAdmin) {
        // For OPERATOR creating STAFF, don't send operatorId (backend will assign)
        delete submitData.operatorId;
      }
      
      await accountsApi.create(submitData);
      alert(t('accountCreatedSuccess'));
      navigate('/accounts');
    } catch (error: unknown) {
      alert(getApiErrorMessage(error, t('errorCreatingAccount')));
    } finally {
      setLoading(false);
    }
  };

  const shouldShowPermissionGroup = () => {
    return formData.role === 'SUPERADMIN' || formData.role === 'STAFF';
  };

  const shouldShowOperatorSelection = () => {
    return formData.role === 'STAFF' && isSuperAdmin;
  };

  const shouldShowBuildingSelection = () => {
    return formData.role === 'STAFF';
  };

  if (loadingData) {
    return (
      <div className="max-w-6xl mx-auto py-8">
        <div className="flex items-center justify-center h-64">
          <div className="text-lg text-gray-600">{t('loading')}</div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto py-8">
      {/* Header */}
      <div className="mb-8">
        <nav className="text-sm text-gray-500 mb-4">
          <span>{t('accountManagement')}</span> / 
          <span className="text-cyan-600 mx-1">{t('accountList')}</span> / 
          <span className="text-gray-900">{t('addAccount')}</span>
        </nav>
        <h1 className="text-3xl font-bold text-gray-900 mb-2">{t('addAccount')}</h1>
        <p className="text-gray-600">{t('createNewAccount')}</p>
      </div>

      <form onSubmit={handleSubmit}>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Left Column - General Information */}
          <div className="space-y-6">
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">{t('generalInfo')}</h2>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('fullName')} *
                  </label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => handleInputChange('name', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
                    placeholder={t('enterFullName')}
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('email')} *
                  </label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => handleInputChange('email', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
                    placeholder={t('enterEmail')}
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('phoneNumber')}
                  </label>
                  <input
                    type="tel"
                    value={formData.phoneNumber || ''}
                    onChange={(e) => handleInputChange('phoneNumber', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
                    placeholder={t('enterPhoneNumber')}
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('roleSelection')} *
                  </label>
                  <select
                    value={formData.role}
                    onChange={(e) => handleInputChange('role', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
                    required
                  >
                    <option value="">{t('selectRole')}</option>
                    {isSuperAdmin && (
                      <>
                        <option value="SUPERADMIN">{t('SUPERADMIN')}</option>
                        <option value="OPERATOR">{t('OPERATOR')}</option>
                      </>
                    )}
                    <option value="STAFF">{t('STAFF')}</option>
                    <option value="RESIDENT">{t('RESIDENT')}</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('statusSelection')}
                  </label>
                  <div className="flex items-center space-x-6">
                    <label className="flex items-center">
                      <input
                        type="radio"
                        name="isActive"
                        value="true"
                        checked={formData.isActive === true}
                        onChange={() => handleInputChange('isActive', true)}
                        className="h-4 w-4 text-cyan-600 focus:ring-cyan-500 border-gray-300"
                      />
                      <span className="ml-2 text-sm text-gray-700">{t('active')}</span>
                    </label>
                    <label className="flex items-center">
                      <input
                        type="radio"
                        name="isActive"
                        value="false"
                        checked={formData.isActive === false}
                        onChange={() => handleInputChange('isActive', false)}
                        className="h-4 w-4 text-cyan-600 focus:ring-cyan-500 border-gray-300"
                      />
                      <span className="ml-2 text-sm text-gray-700">{t('inactive')}</span>
                    </label>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Right Column - Login Information */}
          <div className="space-y-6">
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">{t('loginInformation')}</h2>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('username')} *
                  </label>
                  <input
                    type="text"
                    value={formData.username}
                    onChange={(e) => handleInputChange('username', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
                    placeholder={t('enterUsername')}
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('password')} *
                  </label>
                  <input
                    type="password"
                    value={formData.password}
                    onChange={(e) => handleInputChange('password', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
                    placeholder={t('enterPassword')}
                    required
                  />
                </div>

                {/* Operator Selection - Only for STAFF when SUPERADMIN is creating */}
                {shouldShowOperatorSelection() && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {t('operator')} *
                    </label>
                    <select
                      value={formData.operatorId || ''}
                      onChange={(e) => handleInputChange('operatorId', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
                      required
                    >
                      <option value="">{t('selectOperator')}</option>
                      {operators.map((operator) => (
                        <option key={operator.id} value={operator.id}>
                          {operator.name} ({operator.email})
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                {/* Permission Group - Only for SUPERADMIN and STAFF */}
                {shouldShowPermissionGroup() && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {t('permissionGroup')}
                      {formData.role === 'STAFF' && ' *'}
                    </label>
                    <select
                      value={formData.permissionGroupId || ''}
                      onChange={(e) => handleInputChange('permissionGroupId', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
                      required={formData.role === 'STAFF'}
                    >
                      {formData.role === 'SUPERADMIN' ? (
                        <option value="">{t('fullSystemAccessNoGroup')}</option>
                      ) : (
                        <option value="">{t('selectPermissionGroup')}</option>
                      )}
                      {permissionGroups.map((group) => (
                        <option key={group.id} value={group.id}>
                          {group.name}
                          {group.description && ` - ${group.description}`}
                        </option>
                      ))}
                    </select>
                    {formData.role === 'SUPERADMIN' && (
                      <p className="text-xs text-gray-500 mt-1">{t('superadminPermissionGroupHelp')}</p>
                    )}
                  </div>
                )}

                {/* Building assignment - for STAFF users (Permission Group style) */}
                {shouldShowBuildingSelection() && (
                  <div className="bg-white rounded-lg border border-gray-200 p-4 mt-2">
                    <h3 className="text-md font-semibold text-gray-900 mb-3">{t('applyBuildings')}</h3>
                    <div className="space-y-3">
                      <label className="flex items-center">
                        <input
                          type="radio"
                          checked={applyAllBuildings}
                          onChange={() => setApplyAllBuildings(true)}
                          className="text-cyan-600 focus:ring-cyan-500"
                        />
                        <span className="ml-2 text-sm text-gray-700">{t('allBuildings')}</span>
                      </label>
                      <label className="flex items-center">
                        <input
                          type="radio"
                          checked={!applyAllBuildings}
                          onChange={() => setApplyAllBuildings(false)}
                          className="text-cyan-600 focus:ring-cyan-500"
                        />
                        <span className="ml-2 text-sm text-gray-700">{t('selectBuildings')}</span>
                      </label>
                    </div>
                    {!applyAllBuildings && (
                      <div className="mt-4">
                        {buildings.length > 0 ? (
                          <div className="space-y-2">
                            <p className="text-sm text-gray-600 mb-3">{t('selectBuildingsFromList')}:</p>
                            <div className="max-h-40 overflow-y-auto border border-gray-200 rounded-lg p-3 space-y-2">
                              {buildings.map((b) => {
                                const checked = (formData.buildingIds || []).includes(b.id);
                                return (
                                  <label key={b.id} className="flex items-center">
                                    <input
                                      type="checkbox"
                                      checked={checked}
                                      onChange={(e) => {
                                        const next = new Set(formData.buildingIds || []);
                                        if (e.target.checked) next.add(b.id); else next.delete(b.id);
                                        handleBuildingIdsChange(Array.from(next));
                                      }}
                                      className="text-cyan-600 focus:ring-cyan-500 rounded"
                                    />
                                    <span className="ml-2 text-sm text-gray-700">{b.name}</span>
                                  </label>
                                );
                              })}
                            </div>
                          </div>
                        ) : (
                          <div className="mt-2 text-sm text-gray-600">{t('noBuildings')}</div>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Role Information */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <div className="flex items-start">
                <svg className="w-5 h-5 text-blue-400 mt-0.5 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <div>
                  <h3 className="text-sm font-medium text-blue-900 mb-1">{t('roleInformation')}</h3>
                  <div className="text-sm text-blue-800">
                    {isSuperAdmin && (
                      <>
                        <p>• {t('SUPERADMIN')}: {t('fullSystemAccess')} ({t('fullSystemAccessNoGroup').toLowerCase()})</p>
                        <p>• {t('OPERATOR')}: {t('manageOwnBuildings')}</p>
                      </>
                    )}
                    <p>• {t('STAFF')}: {t('limitedAccessWithPermissions')}</p>
                    <p>• {t('RESIDENT')}: {t('viewOwnInformation')}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Form Actions */}
        <div className="mt-8 flex justify-end space-x-4">
          <button
            type="button"
            onClick={() => navigate('/accounts')}
            className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
          >
            {t('cancel')}
          </button>
          <button
            type="submit"
            disabled={loading}
            className="px-6 py-2 bg-gradient-to-r from-cyan-500 to-blue-500 text-white rounded-lg hover:from-cyan-600 hover:to-blue-600 disabled:opacity-50 transition-all duration-200"
          >
            {loading ? t('creating') : t('save')}
          </button>
        </div>
      </form>
    </div>
  );
}; 